import Config from '@/config';
import NetworkService from '@/services/network.service';
import { AppObject } from '@/types';
import {
  filterToQuery,
  queryToFilter,
  transformObjectToQuery,
} from '@/utility';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { ACTION } from '../action';
import { DEFAULT_ZOOM } from '../constant';
import { IState, SavedSearch } from '../types';
import { usePropertyContext } from './use-property-context';

interface CRMSavedSearch {
  id: string;
  name: string;
  url: string;
  createdOn: string;
}

async function fetchSavedSearchs(): Promise<SavedSearch[]> {
  const service = new NetworkService();
  const result: CRMSavedSearch[] = await service.get('/api/listing/search'); // saved-search

  return result.map((x) => ({
    id: x.id,
    name: x.name,
    createdOn: x.createdOn,
    url: x.url,
  }));
}

async function saveSearchApi(name: string, url: string): Promise<void> {
  const existingSearchList = await fetchSavedSearchs();

  if (existingSearchList.find((x) => x.name === name)) {
    throw new Error('Search with this name already exists');
  }

  const service = new NetworkService();
  await service.post('/api/listing/search', {
    name: name,
    url: url,
  });
}

export const useSavedSearch = () => {
  const context = usePropertyContext();
  const router = useRouter();

  const getAll = useCallback(async () => {
    return fetchSavedSearchs();
  }, []);

  const saveSearch = useCallback(
    async (name: string) => {
      const state = context?.state;
      // save
      // console.log('save', name);
      const query = filterToQuery(state.filter, state.view, state.map.position);
      // console.log(
      //   'save',
      //   `${Config.APP_URL}/properties?${transformObjectToQuery(query)}`
      // );
      await saveSearchApi(
        name,
        `${Config.APP_URL}/properties?${transformObjectToQuery(query)}`
      );
    },
    [context?.state]
  );

  const deleteSearch = useCallback(async (name: string) => {
    const existingSearchList = await fetchSavedSearchs();
    const item = existingSearchList.find((x) => x.name === name);
    if (!item) return;
    const service = new NetworkService();
    await service.delete(`/api/listing/search/${item.id}`);
  }, []);

  const applySearch = useCallback(
    async (savedSerach: SavedSearch) => {
      if (context) {
        const state = context.state;
        const dispatch = context.dispatch;
        const url = new URL(savedSerach.url);

        const query: AppObject = {};

        let search = url.search;
        if (search) {
          search = search.substring(1);
          search.split('&').forEach((x) => {
            const [key, value] = x.split('=');
            if (Object.prototype.hasOwnProperty.call(query, key)) {
              if (Array.isArray(query[key])) {
                query[key] = [...(query[key] as never), value];
              } else {
                query[key] = [query[key] as never, value];
              }
            } else {
              query[key] = value;
            }
          });

          const view = query.view || state.view;
          const isMapView = view === 'map';

          const defaultPosition = { ...state.map.defaultPosition };

          if (isMapView && query.lat && query.lng) {
            defaultPosition.lat = Number(query.lat);
            defaultPosition.lng = Number(query.lng);
            defaultPosition.zoom = query.zoom
              ? Number(query.zoom)
              : DEFAULT_ZOOM;
          }

          dispatch({
            type: ACTION.UPDATE_STATE,
            payload: {
              ...state,
              view,
              filter: {
                ...state.filter,
                ...queryToFilter(query),
              },
              map: {
                ...state.map,
                position: {
                  lat: isMapView ? defaultPosition.lat : null,
                  lng: isMapView ? defaultPosition.lng : null,
                  zoom: isMapView ? defaultPosition.zoom : null,
                },
                defaultPosition,
              },
            } as IState,
          });
        }
      } else {
        const url = new URL(savedSerach.url);
        const pathname = url.pathname;
        const search = url.search;
        const host = window.location.origin;
        const path = `${host}${pathname}${search}`;
        router.push(path);
      }
    },
    [context, router]
  );

  return {
    getAll,
    saveSearch,
    deleteSearch,
    applySearch,
  };
};
